<template>
  <div class="i-navbar-dropdown">
    <div
      ref="dropdownTriggerRef"
      class="dropdown-trigger"
      @click="openDropdown"
      @mouseenter="openDropdown"
    >
      <slot></slot>
    </div>
    <div
      v-if="isDropdownOpen"
      ref="dropdownRef"
      class="dropdown"
      :class="alignClass"
      :style="{ top: `calc(100% + ${props.dropdownMarginTop}px)`, '--sm-width': `${props.screenWidth}px` }"
      @mouseleave="closeDropdown"
    >
      <div class="content">
        <template
          v-for="dropdownItem in Object.keys(dropdownItems)"
          :key="dropdownItem"
        >
          <div class="sub-head">{{ capitalizeWords(dropdownItem) }}</div>
          <template
            v-for="item in dropdownItems[dropdownItem]"
            :key="item.text"
          >
            <div
              v-if="!item.isHidden"
              class="list-item"
              @click="handleItemClick(item)"
            >
              <img
                v-if="item.icons.default"
                class="icon"
                :src="item.isActive ? item.icons.active : item.icons.default"
                :alt="item.text + ' icon'"
              />
              <div
                class="text"
                :style="{ color: item.isActive ? azureRadiance500 : item.textColor || black900 }"
              >
                {{ item.text }}
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, type PropType, computed, onMounted, onBeforeUnmount } from "vue"
import { azureRadiance500, black900 } from "@/styles/colors"

import { capitalizeWords } from "@/composables/capitalizeWords"

import type { NavigationItem } from "@/types/navigationItem"

const isDropdownOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)
const dropdownTriggerRef = ref<HTMLElement | null>(null)

const props = defineProps({
  align: {
    type: String as PropType<"left" | "center" | "right">,
    default: "right"
  },
  dropdownItems: {
    type: Object as () => { [key: string]: NavigationItem[] },
    default: () => {}
  },
  dropdownMarginTop: {
    type: Number,
    default: 32
  },
  screenWidth: {
    type: Number,
    default: null
  }
})

// COMPUTED
const alignClass = computed(() => `align-${props.align}`)

// FUNCTIONS
const openDropdown = () => {
  isDropdownOpen.value = true
}

const closeDropdown = () => {
  isDropdownOpen.value = false
}

const handleClickOutside = (event: MouseEvent) => {
  if (
    dropdownRef.value &&
    !dropdownRef.value.contains(event.target as Node) &&
    dropdownTriggerRef.value &&
    !dropdownTriggerRef.value.contains(event.target as Node)
  ) {
    isDropdownOpen.value = false
  }
}

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    isDropdownOpen.value = false
  }
}

const handleItemClick = (dropdownItem: NavigationItem) => {
  dropdownItem.action()
  isDropdownOpen.value = false // Close dropdown after action
}

// LIFECYCLE HOOKS

onMounted(() => {
  document.addEventListener("click", handleClickOutside)
  document.addEventListener("keydown", handleEscapeKey)
})

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside)
  document.removeEventListener("keydown", handleEscapeKey)
})
</script>

<style scoped lang="scss">
.i-navbar-dropdown {
  position: relative;

  .dropdown-trigger {
    cursor: pointer;
  }

  .dropdown {
    z-index: $z-index-dropdown;
    min-width: 175px;
    position: absolute;
    width: var(--sm-width);
    filter: drop-shadow(0 12px 10px rgb(0 0 0 / 25%));

    @media screen and (min-width: $breakpoint-sm) {
      width: max-content;
      filter: drop-shadow(0 4px 45.5px rgb(0 0 0 / 25%));
    }

    &::before {
      content: "";
      position: absolute;
      top: -9px;
      border-width: 0 9px 9px;
      border-style: solid;
      border-color: transparent transparent #fff;
      left: var(--arrow-left, auto);
      right: var(--arrow-right, auto);
      transform: translateX(var(--arrow-translate-x, 0));
    }

    &.align-left {
      left: 0%;

      --arrow-left: 7px;
      --arrow-right: auto;
      --arrow-translate-x: 0;
    }

    &.align-center {
      left: 50%;
      transform: translateX(-50%);

      --arrow-left: 50%;
      --arrow-right: auto;
      --arrow-translate-x: -50%;
    }

    &.align-right {
      right: -24px;

      --arrow-left: auto;
      --arrow-right: 7px;
      --arrow-translate-x: 0;

      @media screen and (min-width: $breakpoint-sm) {
        right: 0%;
      }
    }

    .content {
      .sub-head,
      .list-item {
        @include flex(row, start, center, $gap: 8px);

        width: 100%;
        padding: 12px;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: 20px;
        text-align: left;
        transition: background-color 0.2s ease;
        background-color: $white;

        &:not(.sub-head) {
          border-top: 1px solid $zumthor100;
          font-weight: 500;
          cursor: pointer;

          &:hover {
            background-color: $zumthor50;
          }
        }

        &:first-child:not(.list-item) {
          @media screen and (min-width: $breakpoint-sm) {
            border-top-left-radius: 12px;
            border-top-right-radius: 12px;
          }
        }

        &:last-child:not(.sub-head) {
          border-bottom-left-radius: 24px;
          border-bottom-right-radius: 24px;

          @media screen and (min-width: $breakpoint-sm) {
            border-bottom-left-radius: 12px;
            border-bottom-right-radius: 12px;
          }
        }

        .icon {
          width: 24px;
          height: 24px;
        }
      }
    }
  }
}
</style>
